import React, { useEffect, useMemo } from "react";
import {
  FormControl,
  IconButton,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Box,
  Button,
  Grid,
  Autocomplete,
} from "@mui/material";
import { formatDate } from "../../../utils/utils";
import {
  smallButtonProp,
  tableCellStyles,
  tableHeaderStyles,
} from "../../../utils/styles";
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Clear as ClearIcon,
  Add as AddIcon,
} from "@mui/icons-material";
import {
  BaseExpenseFieldsProps,
  ExpenseFormData,
  ExpenseEntry,
  PaymentDetail,
} from "./types";
import { FormikErrors, useFormikContext } from "formik";
import { v4 as uuidv4 } from "uuid";

// Payment methods for generic expenses (without advance payment)
const PAYMENT_METHODS: Record<string, string> = {
  CASH: "Cash",
  BANK_TRANSFER: "Bank Transfer",
  UPI: "UPI",
  CHEQUE: "Cheque",
  ONLINE: "Online",
  OTHER: "Other",
};

const getPaymentMethods = () => {
  return ["CASH", "BANK_TRANSFER", "UPI", "CHEQUE", "ONLINE", "OTHER"];
};

interface GenericOtherExpenseFieldsProps extends BaseExpenseFieldsProps {
  masterCategory: string;
  masterReferenceId?: string;
  companyId?: string;
  createdBy?: string;
  defaultDate?: string;
  isEdit?: boolean;
  showDelete?: boolean;
  isRentalExpense?: boolean;
}

const GenericOtherExpenseFields = ({
  masterCategory,
  masterReferenceId,
  companyId,
  createdBy,
  defaultDate,
  isEdit = false,
  showDelete = true,
  isRentalExpense = false,
}: GenericOtherExpenseFieldsProps) => {
  const { values, setFieldValue, errors } = useFormikContext<ExpenseFormData>();
  // Helper function to safely access expense errors
  const getExpenseErrors = (expenseIndex: number) => {
    return errors.expenses?.[expenseIndex] as
      | FormikErrors<ExpenseEntry>
      | undefined;
  };

  // Helper function to safely access payment errors
  const getPaymentErrors = (expenseIndex: number, paymentIndex: number = 0) => {
    const expenseErrors = getExpenseErrors(expenseIndex);
    return expenseErrors?.payments?.[paymentIndex] as
      | FormikErrors<PaymentDetail>
      | undefined;
  };

  const otherExpenseOptions = useMemo(() => {
    return [
      "Ad Blue",
      "Greasing",
      "Oil",
      "Brake Fluid",
      "Coolant",
      "Food Allowance",
      "Mobile Recharge",
      "Loading/Unloading Fees",
      "Night Halt Charges",
      "Emergency Fund Used",
      "Dhobi",
      "Bus Cleaning (Interior)",
      "Medicine",
      "Travel Cost",
      "Toll",
      "RTO Fees",
      "Insurance",
      "Permit Fees",
      "Registration Fees",
    ];
  }, []);

  // Get all "Other" type expenses
  const otherExpenses = values.expenses.filter(
    (expense) => expense.type === "Other"
  );

  const handleOtherExpenseChange = (
    field: string,
    value: string | number,
    expenseIndex: number
  ) => {
    if (expenseIndex !== -1) {
      setFieldValue(`expenses.${expenseIndex}.${field}`, value);
      setFieldValue(`expenses.${expenseIndex}.isEdited`, true);
    }
  };

  const handlePaymentFieldChange = (
    expense: ExpenseEntry,
    field: string,
    value: string,
    expenseIndex: number,
    paymentType: string = "ADVANCE",
    paymentIndex: number = 0
  ) => {
    if (expenseIndex !== -1) {
      // Ensure payments array exists
      if (!values.expenses[expenseIndex].payments) {
        setFieldValue(`expenses.${expenseIndex}.payments`, []);
      }

      // Update payment field
      setFieldValue(
        `expenses.${expenseIndex}.payments.${paymentIndex}.${field}`,
        value
      );
      setFieldValue(
        `expenses.${expenseIndex}.payments.${paymentIndex}.isEdited`,
        true
      );
    }
  };

  const handleEditOtherExpense = (expenseIndex: number, isEditing: boolean) => {
    if (expenseIndex !== -1) {
      setFieldValue(`expenses.${expenseIndex}.isEdited`, isEditing);
    }
  };

  const handleRemoveOtherExpense = (
    expenseIndex: number,
    undo: boolean = false
  ) => {
    if (expenseIndex !== -1) {
      if (undo) {
        setFieldValue(`expenses.${expenseIndex}.isDeleted`, false);
      } else if (values.expenses[expenseIndex]?.isNew) {
        const updatedExpenses = values.expenses.filter(
          (expense, idx) => idx !== expenseIndex
        );
        setFieldValue(`expenses`, updatedExpenses);
      } else {
        setFieldValue(`expenses.${expenseIndex}.isEdited`, false);
        setFieldValue(`expenses.${expenseIndex}.isDeleted`, true);
      }
    }
  };

  const addNewOtherExpense = () => {
    const newExpense: ExpenseEntry = {
      id: uuidv4(),
      type: "Other",
      expenseDate: defaultDate || new Date().toISOString().split("T")[0],
      amount: "0",
      paidTo: "",
      contactNumber: "",
      location: "",
      notes: "",
      fuelType: "",
      liters: "",
      serviceType: "",
      serviceProvider: "",
      category: "Custom",
      customCategory: "",
      challanAction: "",
      payments: [
        {
          type: "ADVANCE",
          mode: "CASH",
          amount: 0,
          date: defaultDate || new Date().toISOString().split("T")[0],
          location: "",
          receivedFrom: "",
          remarks: "",
          status: "PENDING",
          isEdited: false,
          isDeleted: false,
          isNew: true,
        },
      ],
      selectedFiles: [],
      uploadedFiles: [],
      isNew: true,
      isEdited: false,
      isDeleted: false,
      masterReferenceId: masterReferenceId || null,
      masterCategory: masterCategory,
      companyId: companyId || null,
      createdBy: createdBy || null,
    };

    setFieldValue("expenses", [...values.expenses, newExpense]);
  };

  return (
    <Grid container item xs={12} spacing={1} sx={{ my: 1 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
          width: "100%",
        }}
      >
        <Typography variant="h6" color="primary" sx={{ fontSize: "0.85rem" }}>
          Other Expenses
        </Typography>
      </Box>
      <TableContainer
        sx={{
          borderRadius: 1,
          border: "1px solid",
          borderColor: "grey.300",
          bgcolor: "#fdfdfd",
        }}
      >
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell style={tableHeaderStyles}>Expense Name</TableCell>
              <TableCell style={tableHeaderStyles}>Amount</TableCell>
              <TableCell style={tableHeaderStyles}>Payment Date</TableCell>
              <TableCell style={tableHeaderStyles}>Payment Method</TableCell>
              <TableCell style={tableHeaderStyles}>Location</TableCell>
              <TableCell style={tableHeaderStyles}>Notes</TableCell>
              {showDelete && (
                <TableCell style={tableHeaderStyles}>Actions</TableCell>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {otherExpenses.map((expense, expenseIndex) => {
              const actualIndex = values.expenses.findIndex(
                (e) => e === expense
              );
              const isDeleted = Boolean(expense?.isDeleted);
              const isNew = Boolean(expense?.isNew);
              const isEditing = Boolean(expense?.isEdited);

              // Ensure payment record exists for other expenses
              if (!expense?.payments || expense?.payments?.length === 0) {
                expense.payments = [
                  {
                    type: "ADVANCE",
                    mode: "CASH",
                    amount: Number(expense?.amount) || 0,
                    date:
                      expense?.expenseDate ||
                      defaultDate ||
                      new Date().toISOString().split("T")[0],
                    location: expense?.location || "",
                    receivedFrom: "",
                    remarks: "",
                    status: "PENDING",
                    isEdited: false,
                    isDeleted: false,
                    isNew: true,
                  },
                ];
              }

              return (
                <TableRow
                  key={actualIndex}
                  sx={{
                    backgroundColor: isDeleted ? "#fff5f5" : "inherit",
                    "&:hover": {
                      backgroundColor: isDeleted ? "#fff0f0" : "#f5f5f5",
                    },
                    "& .MuiTableCell-root": {
                      borderBottom: "1px solid",
                      borderColor: "grey.100",
                    },
                  }}
                >
                  <TableCell>
                    {!isDeleted ? (
                      <Autocomplete
                        freeSolo
                        options={otherExpenseOptions}
                        value={expense?.paidTo || ""}
                        getOptionLabel={(option) => option}
                        onChange={(e, value) => {
                          handleOtherExpenseChange(
                            "paidTo",
                            value || "",
                            actualIndex
                          );
                          handleOtherExpenseChange(
                            "customCategory",
                            value || "",
                            actualIndex
                          );
                        }}
                        onInputChange={(_, value, reason) => {
                          if (reason === "input") {
                            handleOtherExpenseChange(
                              "paidTo",
                              value || "",
                              actualIndex
                            );
                            handleOtherExpenseChange(
                              "customCategory",
                              value || "",
                              actualIndex
                            );
                          }
                        }}
                        renderOption={(props, option) => (
                          <MenuItem
                            {...props}
                            sx={{ fontSize: "0.65rem" }}
                            key={option}
                          >
                            {option}
                          </MenuItem>
                        )}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            size="small"
                            placeholder="Expense item name"
                            variant="standard"
                            sx={{
                              minWidth: "120px",
                              "& .MuiInputBase-input": {
                                ...tableCellStyles,
                                padding: "2px 0",
                              },
                              "& .MuiInput-underline:before": {
                                borderBottomColor: "rgba(185, 185, 185, 0.42)",
                                borderWidth: "0.25px",
                              },
                              "& .MuiInput-underline:hover:not(.Mui-disabled):before":
                                {
                                  borderBottomColor: "rgba(0, 0, 0, 0.87)",
                                  borderWidth: "0.25px",
                                },
                              "& .MuiInput-underline:after": {
                                borderBottomColor: "#1976d2",
                              },
                              "& .MuiFormHelperText-root": {
                                fontSize: "0.55rem",
                              },
                            }}
                            error={Boolean(
                              getExpenseErrors(actualIndex)?.paidTo
                            )}
                            helperText={
                              getExpenseErrors(actualIndex)?.paidTo
                                ? "Expense type is required"
                                : ""
                            }
                          />
                        )}
                      />
                    ) : (
                      <Typography
                        sx={{
                          ...tableCellStyles,
                          fontStyle: isDeleted ? "italic" : "normal",
                        }}
                      >
                        {expense?.paidTo || "N/A"}
                      </Typography>
                    )}
                  </TableCell>

                  <TableCell>
                    {!isDeleted ? (
                      <TextField
                        size="small"
                        variant="standard"
                        type="number"
                        value={expense?.amount || ""}
                        onChange={(e) => {
                          const value = e.target.value;
                          const formattedValue =
                            value === "0" ? "0" : value.replace(/^0+/, "");
                          const newAmount = Number(formattedValue) || 0;

                          // Update expense amount and payment amount
                          handleOtherExpenseChange(
                            "amount",
                            newAmount,
                            actualIndex
                          );
                          handlePaymentFieldChange(
                            expense,
                            "amount",
                            newAmount.toString(),
                            actualIndex,
                            "ADVANCE",
                            0
                          );
                        }}
                        InputProps={{
                          startAdornment: (
                            <span
                              style={{ marginRight: 2, ...tableCellStyles }}
                            >
                              ₹
                            </span>
                          ),
                        }}
                        sx={{
                          ...tableCellStyles,
                          minWidth: "80px",
                          "& .MuiInputBase-input": {
                            ...tableCellStyles,
                            padding: "2px 0",
                          },
                          "& .MuiInput-underline:before": {
                            borderBottomColor: "rgba(185, 185, 185, 0.42)",
                            borderWidth: "0.25px",
                          },
                          "& .MuiInput-underline:hover:not(.Mui-disabled):before":
                            {
                              borderBottomColor: "rgba(0, 0, 0, 0.87)",
                              borderWidth: "0.25px",
                            },
                          "& .MuiInput-underline:after": {
                            borderBottomColor: "#1976d2",
                          },
                          "& input[type=number]": {
                            MozAppearance: "textfield",
                            "&::-webkit-outer-spin-button": {
                              WebkitAppearance: "none",
                              margin: 0,
                            },
                            "&::-webkit-inner-spin-button": {
                              WebkitAppearance: "none",
                              margin: 0,
                            },
                          },
                          "& .MuiFormHelperText-root": {
                            fontSize: "0.55rem",
                          },
                        }}
                        onWheel={(e) =>
                          e?.target instanceof HTMLElement && e?.target.blur()
                        }
                        error={Boolean(getExpenseErrors(actualIndex)?.amount)}
                        helperText={getExpenseErrors(actualIndex)?.amount}
                      />
                    ) : (
                      <Typography
                        sx={{
                          ...tableCellStyles,
                          fontStyle: isDeleted ? "italic" : "normal",
                        }}
                      >{`₹${expense?.amount || 0}`}</Typography>
                    )}
                  </TableCell>

                  <TableCell>
                    {!isDeleted ? (
                      <TextField
                        size="small"
                        variant="standard"
                        type="date"
                        value={
                          expense.payments[0]?.date
                            ? expense.payments[0].date.split("T")[0]
                            : ""
                        }
                        onChange={(e) => {
                          handleOtherExpenseChange(
                            "expenseDate",
                            e.target.value,
                            actualIndex
                          );
                          handlePaymentFieldChange(
                            expense,
                            "date",
                            e?.target?.value,
                            actualIndex,
                            "ADVANCE",
                            0
                          );
                        }}
                        sx={{
                          ...tableCellStyles,
                          minWidth: "80px",
                          "& .MuiInputBase-input": {
                            ...tableCellStyles,
                            padding: "2px 0",
                          },
                          "& .MuiInput-underline:before": {
                            borderBottomColor: "rgba(185, 185, 185, 0.42)",
                            borderWidth: "0.25px",
                          },
                          "& .MuiInput-underline:hover:not(.Mui-disabled):before":
                            {
                              borderBottomColor: "rgba(0, 0, 0, 0.87)",
                              borderWidth: "0.25px",
                            },
                          "& .MuiInput-underline:after": {
                            borderBottomColor: "#1976d2",
                          },
                          "& .MuiFormHelperText-root": {
                            fontSize: "0.55rem",
                          },
                        }}
                        error={Boolean(getPaymentErrors(actualIndex, 0)?.date)}
                        helperText={getPaymentErrors(actualIndex, 0)?.date}
                      />
                    ) : (
                      <Typography
                        sx={{
                          ...tableCellStyles,
                          fontStyle: isDeleted ? "italic" : "normal",
                        }}
                      >
                        {expense?.payments[0]?.date
                          ? expense.payments[0].date.includes("T")
                            ? formatDate(expense.payments[0].date)
                            : expense.payments[0].date
                          : "N/A"}
                      </Typography>
                    )}
                  </TableCell>

                  <TableCell>
                    {!isDeleted ? (
                      <FormControl
                        fullWidth
                        size="small"
                        variant="standard"
                        sx={{
                          "& .MuiInput-underline:before": {
                            borderBottom: "none",
                          },
                          "& .MuiInput-underline:hover:not(.Mui-disabled):before":
                            {
                              borderBottom: "none",
                            },
                          "& .MuiInput-underline:after": {
                            borderBottom: "none",
                          },
                        }}
                      >
                        <Select
                          value={expense?.payments[0]?.mode || "CASH"}
                          onChange={(e) => {
                            handlePaymentFieldChange(
                              expense,
                              "mode",
                              e?.target?.value,
                              actualIndex,
                              "ADVANCE",
                              0
                            );
                          }}
                          displayEmpty
                          sx={{
                            backgroundColor: "#fff",
                            ...tableCellStyles,
                            minHeight: "28px",
                            "& .MuiSelect-select": {
                              ...tableCellStyles,
                              padding: "4px 8px",
                            },
                          }}
                        >
                          {getPaymentMethods().map((type) => (
                            <MenuItem
                              key={type}
                              value={type}
                              sx={{ fontSize: "0.75rem" }}
                            >
                              {PAYMENT_METHODS[type]}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    ) : (
                      <Typography sx={{ ...tableCellStyles }}>
                        {expense?.payments[0]?.mode
                          ? PAYMENT_METHODS[expense.payments[0].mode]
                          : "N/A"}
                      </Typography>
                    )}
                  </TableCell>

                  <TableCell>
                    {!isDeleted ? (
                      <TextField
                        size="small"
                        variant="standard"
                        value={expense?.location || ""}
                        onChange={(e) => {
                          handleOtherExpenseChange(
                            "location",
                            e.target.value,
                            actualIndex
                          );
                        }}
                        placeholder="Location"
                        sx={{
                          minWidth: "100px",
                          "& .MuiInputBase-input": {
                            ...tableCellStyles,
                            padding: "2px 0",
                          },
                          "& .MuiInput-underline:before": {
                            borderBottomColor: "rgba(185, 185, 185, 0.42)",
                            borderWidth: "0.25px",
                          },
                          "& .MuiInput-underline:hover:not(.Mui-disabled):before":
                            {
                              borderBottomColor: "rgba(0, 0, 0, 0.87)",
                              borderWidth: "0.25px",
                            },
                          "& .MuiInput-underline:after": {
                            borderBottomColor: "#1976d2",
                          },
                        }}
                      />
                    ) : (
                      <Typography
                        sx={{
                          ...tableCellStyles,
                          fontStyle: isDeleted ? "italic" : "normal",
                        }}
                      >
                        {expense?.location || "N/A"}
                      </Typography>
                    )}
                  </TableCell>

                  <TableCell>
                    {!isDeleted ? (
                      <TextField
                        size="small"
                        variant="standard"
                        value={expense?.notes || ""}
                        onChange={(e) =>
                          handleOtherExpenseChange(
                            "notes",
                            e.target.value,
                            actualIndex
                          )
                        }
                        placeholder="Notes"
                        sx={{
                          minWidth: "120px",
                          "& .MuiInputBase-input": {
                            ...tableCellStyles,
                            padding: "2px 0",
                          },
                          "& .MuiInput-underline:before": {
                            borderBottomColor: "rgba(185, 185, 185, 0.42)",
                            borderWidth: "0.25px",
                          },
                          "& .MuiInput-underline:hover:not(.Mui-disabled):before":
                            {
                              borderBottomColor: "rgba(0, 0, 0, 0.87)",
                              borderWidth: "0.25px",
                            },
                          "& .MuiInput-underline:after": {
                            borderBottomColor: "#1976d2",
                          },
                        }}
                      />
                    ) : (
                      <Typography
                        sx={{
                          ...tableCellStyles,
                          fontStyle: isDeleted ? "italic" : "normal",
                        }}
                      >
                        {expense?.notes || "N/A"}
                      </Typography>
                    )}
                  </TableCell>

                  {showDelete && (
                    <TableCell>
                      {isDeleted ? (
                        <Typography
                          sx={{
                            fontStyle: "italic",
                            ...tableCellStyles,
                            color: "red",
                            cursor: "pointer",
                            "&:hover": {
                              textDecoration: "underline",
                            },
                          }}
                          onClick={() =>
                            handleRemoveOtherExpense(actualIndex, true)
                          }
                        >
                          Undo
                        </Typography>
                      ) : (
                        <IconButton
                          size="small"
                          sx={{ p: 0 }}
                          onClick={() => handleRemoveOtherExpense(actualIndex)}
                          disabled={false}
                        >
                          <DeleteIcon
                            sx={{ fontSize: "0.95rem" }}
                            color="error"
                          />
                        </IconButton>
                      )}
                    </TableCell>
                  )}
                </TableRow>
              );
            })}
            {otherExpenses.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} sx={{ textAlign: "center" }}>
                  No other expenses found
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {!isEdit && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            mt: 1,
            width: "100%",
          }}
        >
          <Button
            startIcon={<AddIcon />}
            onClick={addNewOtherExpense}
            variant="outlined"
            {...smallButtonProp}
          >
            Add Other Expense
          </Button>
        </Box>
      )}
    </Grid>
  );
};

export default GenericOtherExpenseFields;
